import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useScreenWidth } from "@msys/ui";
import { Container } from "@mui/material";
import React from "react";
import { useFeature } from "../../../common/FeatureFlags.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { DecisionsInBulkProcessRef } from "../../features/doc-items/modals/DecisionsInBulkProcess.js";
import { RequirementAction } from "../../features/requirements/RequirementAction.js";
import { RequirementDecisionsInBulkProcess } from "../../features/requirements/RequirementDecisionsInBulkProcess.js";
import { RequirementPreview } from "../../features/requirements/RequirementPreview.js";
import { RequirementSubHeader } from "../../features/requirements/RequirementSubHeader.js";
import { DocShareBox } from "../documents/DocShareBox.js";
import { useProjectRequirementPreviewQuery } from "./ProjectRequirementPreview.generated.js";

const EMPTY_ARRAY: any[] = [];

interface Props {
  projectId: string;
  requirementId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export const ProjectRequirementPreview = ({
  projectId,
  requirementId,
  submenuItems,
  activeSubmenuItem,
  prefixBreadcrumbs,
}: Props) => {
  const viewer = useUserData().currentUser!;
  const { isMinTablet } = useScreenWidth();

  const [previewContainer, setPreviewContainer] =
    React.useState<HTMLDivElement | null>(null);

  const client = useApolloClient();
  const query = useProjectRequirementPreviewQuery({
    client,
    variables: {
      quoteId: requirementId,
      projectId,
    },
    fetchPolicy: "no-cache",
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.requirement)?.requirement;
  const docTitle = doc?.title ?? "";
  const rootItem = doc?.rootItem;

  const pathToProject = `/projects/${projectId}`;
  const pathToDocList = `/projects/${projectId}/requirements`;
  const pathToDoc = `/projects/${projectId}/requirements/${requirementId}`;
  const pathToDocPage = `${pathToDoc}/preview`;

  const canFinalize =
    viewer.organisation.id === doc?.owningSystemOrganisationId;

  const canEdit = viewer.organisation.id === doc?.owningSystemOrganisationId;

  const docSharingEnabled = useFeature("DocSharing");
  const viewerPermissionDetailsEnabled = useFeature("ViewerPermissionDetails");

  const decisionsInBulkProcessRef =
    React.useRef<DecisionsInBulkProcessRef>(null);
  const startDecisionsInBulkProcess = React.useCallback(() => {
    decisionsInBulkProcessRef.current?.startDecisionsInBulkProcess();
  }, []);

  return (
    <Page
      subtitle={project?.title}
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && doc && rootItem ? (
          <RequirementSubHeader
            project={project}
            requirement={doc}
            activeView="preview"
            canEdit={canEdit}
            pathToDoc={pathToDoc}
            pathToDocList={pathToDocList}
            pathToDocPage={pathToDocPage}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
          />
        ) : undefined
      }
      action={
        project && doc ? (
          <RequirementAction
            doc={doc}
            project={project}
            pathToProject={pathToProject}
            startDecisionsInBulkProcess={startDecisionsInBulkProcess}
          />
        ) : undefined
      }
    >
      {project && doc && rootItem && (
        <PageContainer ref={setPreviewContainer}>
          <Container maxWidth="md">
            {viewerPermissionDetailsEnabled ? (
              <div>
                <h2>viewer permission details</h2>
                <hr />
                <textarea
                  style={{ width: "100%", height: 50 }}
                  readOnly
                  defaultValue={JSON.stringify(doc.viewerPermissions, null, 2)}
                ></textarea>
              </div>
            ) : null}

            {docSharingEnabled ? (
              <DocShareBox
                docType="REQUIREMENT"
                docId={requirementId}
                projectId={projectId}
              />
            ) : null}

            <RequirementPreview
              requirement={doc}
              canFinalize={canFinalize}
              canEdit={canEdit}
              expandedItemIds={EMPTY_ARRAY}
              refetch={query.refetch}
              container={isMinTablet ? previewContainer : undefined}
            />
            <RequirementDecisionsInBulkProcess
              ref={decisionsInBulkProcessRef}
              projectId={projectId}
              requirementId={doc.id}
              canFinalize={canFinalize}
              externalExpandedItemIds={EMPTY_ARRAY}
              handleComplete={query.refetch}
            />
          </Container>
        </PageContainer>
      )}
    </Page>
  );
};
